import React, { useState, useEffect, useRef } from 'react';
import { TextField, TextFieldProps, Tooltip } from '@mui/material';
import { getPreferredUnits } from '../../Services/Global';
import {
    convertCelsiusToFahrenheit,
    convertFahrenheitToCelsius,
    convertKilogramsToPounds,
    convertPoundsToKilograms,
    convertM3hToGpm,
    convertGpmToM3h,
    convertKpaToPsi,
    convertPsiToKpa,
    convertMsToFps,
    convertFpsToMs,
    UnitType
} from '../../Utils/UnitsHelper';

export interface IUnitAwareTextFieldProps extends Omit<TextFieldProps, 'value' | 'onChange' | 'ref'> {
    /** The quantity being edited, or null when dimensionless (never converted). */
    unitType: UnitType | null;
    metricValue?: number | string;
    onMetricValueChange?: (metricValue: number | string) => void;
    roundDisplayValue?: boolean;
    tooltipContent?: string;
}

/**
 * A TextField wrapper that automatically converts between metric and US units
 * based on the user's preferred units setting.
 * The component always communicates via `metricValue` to the parent.
 * It also includes the "NoScroll" behavior to prevent accidental value changes on mouse wheel.
 */
export const UnitAwareTextField: React.FC<IUnitAwareTextFieldProps> = ({
    unitType,
    metricValue,
    onMetricValueChange,
    roundDisplayValue = false,
    tooltipContent = '',
    onFocus,
    onBlur,
    InputProps,
    inputProps,
    ...rest
}) => {
    const isUS = getPreferredUnits().preferredUnits === 'us';
    const [displayValue, setDisplayValue] = useState<string>('');
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLDivElement>(null);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const tooltipColor = rest.name?.substring(0, 3) === "Hot" ? "rgba(91,28,27,0.8)" : "rgba(0,127,200,0.8)";

    // NoScroll behavior
    useEffect(() => {
        const divElement = inputRef.current;
        if (divElement) {
            const inputElement = divElement.querySelector('input');
            if (inputElement) {
                const handleWheel = (e: WheelEvent) => {
                    e.preventDefault();
                };
                
                inputElement.addEventListener('wheel', handleWheel, { passive: false });
                
                return () => {
                    inputElement.removeEventListener('wheel', handleWheel);
                };
            }
        }
        return undefined;
    }, []);

    // Tooltip hover behavior
    useEffect(() => {
        if (!tooltipContent || tooltipContent.length === 0) {
            return;
        }

        const divElement = inputRef.current;
        if (!divElement) {
            return;
        }

        const handleMouseEnter = () => {
            setTooltipOpen(true);
        };

        const handleMouseLeave = () => {
            setTooltipOpen(false);
        };

        divElement.addEventListener('mouseenter', handleMouseEnter);
        divElement.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            divElement.removeEventListener('mouseenter', handleMouseEnter);
            divElement.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, [tooltipContent]);

    // Convert metric to US for display
    const convertToDisplay = (metricVal: number | string | undefined): string => {
        if (metricVal === undefined || metricVal === null || metricVal === '') {
            return '';
        }

        const numVal = typeof metricVal === 'string' ? parseFloat(metricVal) : metricVal;
        if (isNaN(numVal)) return metricVal.toString();

        if (unitType === null || !isUS) {
            return numVal.toString();
        }

        let converted = numVal;
        switch (unitType) {
            case 'temperature':
                converted = convertCelsiusToFahrenheit(numVal, roundDisplayValue);
                break;
            case 'massFlow':
                converted = convertKilogramsToPounds(numVal, roundDisplayValue);
                break;
            case 'volumetricFlow':
                converted = convertM3hToGpm(numVal, roundDisplayValue);
                break;
            case 'pressure':
                converted = convertKpaToPsi(numVal, roundDisplayValue);
                break;
            case 'velocity':
                converted = convertMsToFps(numVal, roundDisplayValue);
                break;
        }

        // Avoid long decimals if not explicitly rounded, but handle perfect integers
        if (roundDisplayValue) {
            return converted.toString();
        } else {
            // Use toFixed(4) to strip tiny floating point errors (like 138.00000000000003), 
            // but use parseFloat to remove trailing zeros (so 138.0000 becomes 138)
            return parseFloat(converted.toFixed(4)).toString();
        }
    };

    // Convert US back to metric for the parent
    const convertToMetric = (displayVal: string): number | string => {
        if (displayVal === '') return '';

        const numVal = parseFloat(displayVal);
        if (isNaN(numVal)) return displayVal;

        if (unitType === null || !isUS) {
            return numVal;
        }

        let converted = numVal;
        switch (unitType) {
            case 'temperature':
                converted = convertFahrenheitToCelsius(numVal, false);
                break;
            case 'massFlow':
                converted = convertPoundsToKilograms(numVal, false);
                break;
            case 'volumetricFlow':
                converted = convertGpmToM3h(numVal, false);
                break;
            case 'pressure':
                converted = convertPsiToKpa(numVal, false);
                break;
            case 'velocity':
                converted = convertFpsToMs(numVal, false);
                break;
        }

        // DO NOT ROUND HERE! Let the backend get the exact mathematical equivalent.
        // Rounding here causes round-trip precision loss (e.g. 12151.8 -> 5511.9638 -> 12151.8001)
        return converted;
    };

    // Sync display value when metric value changes from outside
    useEffect(() => {
        // Only override the user's active typing if the component is NOT focused.
        // This prevents the cursor from jumping and allows typing decimals (e.g. "1.")
        if (!isFocused) {
            setDisplayValue(convertToDisplay(metricValue));
        }
    }, [metricValue, isUS, unitType, roundDisplayValue, isFocused]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const val = event.target.value ?? '';
        setDisplayValue(val);

        if (onMetricValueChange) {
            onMetricValueChange(convertToMetric(val));
        }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setIsFocused(true);
        if (tooltipContent && e.target.value === '') {
            setTooltipOpen(true);
        }
        if (onFocus) {
            onFocus(e);
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setIsFocused(false);
        setTooltipOpen(false);
        if (onBlur) {
            onBlur(e);
        }
    };

    const textField = (
        <TextField
            {...rest}
            ref={inputRef}
            value={displayValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            InputProps={{
                ...InputProps,
            }}
            inputProps={{
                ...inputProps,
                step: inputProps?.step || 'any',
                autoComplete: 'off',
            }}
        />
    );

    if (tooltipContent && tooltipContent.length > 0) {
        return (
            <Tooltip 
                title={tooltipContent}
                open={tooltipOpen}
                disableFocusListener
                disableTouchListener
                disableHoverListener
                arrow
                componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: tooltipColor,
                        color: '#fff',
                      }
                    },
                    arrow: {
                      sx: {
                        color: tooltipColor,
                      }
                    }
                  }}
            >
                {textField}
            </Tooltip>
        );
    }
    return textField;
};
